import {Button, IconButton, TextField} from '@wandb/components';
import React from 'react';

import {useDebounceState} from '../../../util/hooks';
import {maybePluralize} from '../../../util/uihelpers';
import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {FOOTER_ACTIONS_HEIGHT} from './constants';
import {useCreateMultiMetricPanels} from './hooks/useCreateMultiMetricPanels';
import {useMultiMetricPanelsMap} from './hooks/useMultiMetricPanelsMap';
import {MultiMetricPanelsList} from './MultiMetricPanelsList';
import {usePanelsQuickAddContext} from './PanelsQuickAddContext';
import {validateMultiMetricQuickAddRegex} from './utils/regex';

type MultiMetricPanelAddProps = {
  onBack: () => void;
  initialRegex?: string;
};

export const MultiMetricPanelAdd = ({
  onBack,
  initialRegex,
}: MultiMetricPanelAddProps) => {
  const {closeQuickAdd} = usePanelsQuickAddContext();
  const [regex, debouncedRegex, setRegex] = useDebounceState(
    initialRegex ?? '',
    500
  );
  const {errorMessage: regexValidationError} =
    validateMultiMetricQuickAddRegex(regex);
  const {
    map: panelsToAdd,
    numNotAdded,
    numAlreadyAdded,
  } = useMultiMetricPanelsMap(debouncedRegex, !regexValidationError);
  const onCreatePanels = useCreateMultiMetricPanels(panelsToAdd);

  return (
    <div>
      <div className="border-b border-solid border-moon-250 px-24 ">
        <div className="flex items-center justify-between py-16">
          <IconButton
            variant="ghost"
            icon="back"
            aria-label="Back"
            onPress={onBack}
          />
          <IconButton
            variant="ghost"
            icon="close"
            aria-label="Close"
            onPress={closeQuickAdd}
          />
        </div>
        <h2 className="text-xl font-semibold leading-[32px]">
          Create multi-metric panels
        </h2>
        <p className="pb-16 text-moon-500">
          Use regex to group multiple metrics within the same panels.
        </p>
      </div>
      <WorkspaceDrawerScroller
        className="p-24 flex flex-col gap-24"
        footerHeight={FOOTER_ACTIONS_HEIGHT}>
        <TextField
          label="Regex"
          value={regex}
          onChange={setRegex}
          validate={() => regexValidationError ?? true}
          fullWidth
        />
        {panelsToAdd.size > 0 && (
          <MultiMetricPanelsList
            panelsToAdd={panelsToAdd}
            numNotAdded={numNotAdded}
            numAlreadyAdded={numAlreadyAdded}
          />
        )}
      </WorkspaceDrawerScroller>
      <div className="border-t border-solid border-moon-250 p-16">
        <Button
          fullWidth
          isDisabled={numNotAdded === 0}
          onPress={onCreatePanels}>
          {numNotAdded === 0
            ? 'Create panels'
            : `Create ${maybePluralize(numNotAdded, 'panel')}`}
        </Button>
      </div>
    </div>
  );
};
